import { StyleProps, useMediaQuery } from "@chakra-ui/react";
import React, { memo, useCallback, useEffect, useState } from "react";
import { IconType } from "react-icons";
import { HiChevronLeft, HiChevronRight } from "react-icons/hi";
import "react-lazy-load-image-component/src/effects/blur.css";
import { AppModal, Row, UiBox, UiCenter, UiIcon, UiImage, UiSpinner, UiText } from "~/components/elements";
import { QUERY_MOBILE } from "~/constants";
import { EChevronType, ModalProps } from "~/types/general";

interface Props extends ModalProps {
  imageSrc?: string;
  handleOnClickChevron: (chevronType: EChevronType) => void;
  totalItems?: number;
  itemNth?: number;
}

interface GalleryChevronProps extends StyleProps {
  icon: IconType;
  type: EChevronType;
  handleOnClick: (chevronType: EChevronType) => void;
}

function GalleryChevron({ icon, type, handleOnClick, ...rest }: GalleryChevronProps) {
  return (
    <UiCenter
      bg="blue"
      borderRadius="50%"
      w={{ base: "45px", md: "48px" }}
      h={{ base: "45px", md: "48px" }}
      cursor="pointer"
      transition="transform .2s ease"
      _hover={{ transform: "scale(1.05)" }}
      onClick={e => {
        e.stopPropagation();
        handleOnClick(type);
      }}
      {...rest}
    >
      <UiIcon as={icon} boxSize="40px" color="neutral.gray00" />
    </UiCenter>
  );
}

function ResultImageView({ imageSrc, handleOnClickChevron, visible, onClose, itemNth, totalItems }: Props) {
  const [isDesktop] = useMediaQuery(`(min-width: ${QUERY_MOBILE})`, {
    ssr: false,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isZoom, setIsZoom] = useState(!isDesktop);
  const imgSize = !isDesktop ? { width: "93%" } : { height: isZoom ? "85vh" : "85vh" };
  const handleUserPressArrow = useCallback(
    (event: KeyboardEvent) => {
      const { key } = event;
      if (key === "ArrowRight") {
        handleOnClickChevron(EChevronType.Next);
      }
      if (key === "ArrowLeft") {
        handleOnClickChevron(EChevronType.Previous);
      }
    },
    [handleOnClickChevron]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleUserPressArrow);
    return () => {
      window.removeEventListener("keydown", handleUserPressArrow);
    };
  }, [handleUserPressArrow]);

  useEffect(() => {
    setIsLoading(true);
  }, [imageSrc]);

  return (
    <AppModal
      isOpen={visible}
      closeOnEsc
      closeOnOverlayClick
      hideClose
      header={null}
      hideSubmit
      showFooter={false}
      showRightConnerCloseButton={false}
      contentMinW="20%"
      contentMaxW="100vw"
      modalBodyProps={{ padding: 0 }}
      modalContentProps={{ background: "transparent", boxShadow: "none" }}
      onClose={onClose}
      isCentered
    >
      <Row onClick={onClose} px="20px" align="center" justifyContent="space-between">
        {isDesktop && (
          <GalleryChevron icon={HiChevronLeft} type={EChevronType.Previous} handleOnClick={handleOnClickChevron} />
        )}
        <UiBox
          // _hover={{ cursor: isZoom ? "zoom-out" : "zoom-in" }}
          pos="relative"
          minW="200px"
          minH="200px"
          style={{ margin: "auto" }}
        >
          {itemNth && totalItems && (
            <Row position="absolute" top={-8} left={0} right={0} justifyContent="center" align="center">
              <UiText fontSize="lg" fontWeight="bold" color="white" mx="20px">
                {" "}
                {itemNth} / {totalItems}
              </UiText>
            </Row>
          )}
          <UiCenter position="absolute" display={isLoading ? "flex" : "none"} top={0} right={0} bottom={0} left={0}>
            <UiSpinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
          </UiCenter>
          <UiImage
            onClick={e => {
              e.stopPropagation();
              // if (isDesktop) {
              //   setIsZoom(prev => !prev);
              // }
            }}
            style={{ margin: "auto", userSelect: "none", ...imgSize }}
            src={imageSrc}
            onLoad={() => setIsLoading(false)}
            alt=""
          />
          {!isDesktop && (
            <Row position="absolute" bottom={-24} left={0} right={0} justifyContent="center" align="center">
              <GalleryChevron
                icon={HiChevronLeft}
                type={EChevronType.Previous}
                handleOnClick={handleOnClickChevron}
                mr={7}
              />
              <GalleryChevron
                icon={HiChevronRight}
                type={EChevronType.Next}
                handleOnClick={handleOnClickChevron}
                ml={7}
              />
            </Row>
          )}
        </UiBox>
        {isDesktop && (
          <GalleryChevron icon={HiChevronRight} type={EChevronType.Next} handleOnClick={handleOnClickChevron} />
        )}
      </Row>
    </AppModal>
  );
}

export default memo(ResultImageView);
